// PHASE_0 B0 deliverable 2 — application-composite loading-state primitive.
//
// The in-flight counterpart to Empty/ErrorState. Per spec 43 loading_state
// AC ("skeletons first, spinner only when skeletons would mislead") it
// renders skeleton rows by default and a Spinner when `variant="spinner"`.
// Composes the existing Skeleton + Spinner primitives — does not reinvent
// them. The accessible label is caller-supplied so screen readers announce
// what is loading (host owns the i18n key).

import { forwardRef, type HTMLAttributes } from "react";

import { cx } from "../lib/cx.ts";
import { Skeleton } from "../Skeleton/index.ts";
import { Spinner } from "../Spinner/index.ts";

export type LoadingVariant = "skeleton" | "spinner";

export interface LoadingProps extends HTMLAttributes<HTMLDivElement> {
  /** Render mode — skeleton rows (default) or a centered spinner. */
  readonly variant?: LoadingVariant;
  /** Number of skeleton rows when variant='skeleton'. Defaults to 3. */
  readonly rows?: number;
  /** Required accessible label — what is loading? */
  readonly label: string;
}

export const Loading = forwardRef<HTMLDivElement, LoadingProps>(
  ({ variant = "skeleton", rows = 3, label, className, ...rest }, ref) => {
    if (variant === "spinner") {
      return (
        <div
          ref={ref}
          data-component="loading"
          data-variant="spinner"
          className={cx("nai-loading", className)}
          {...rest}
        >
          <Spinner aria-label={label} />
        </div>
      );
    }
    return (
      <div
        ref={ref}
        role="status"
        aria-busy="true"
        aria-label={label}
        data-component="loading"
        data-variant="skeleton"
        className={cx("nai-loading", className)}
        {...rest}
      >
        {Array.from({ length: Math.max(1, rows) }, (_unused, i) => (
          <Skeleton key={i} shape="line" aria-label={label} />
        ))}
      </div>
    );
  },
);
Loading.displayName = "Loading";
